import { format, formatDistanceToNowStrict } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Play } from "lucide-react";

import {
  SUPPORT_REASON_LABEL,
  SUPPORT_STATUS_BADGE_CLASSNAME,
  SUPPORT_STATUS_LABEL,
  SupportRequest,
} from "@/app/entities/SupportRequest";
import { cn } from "@/app/utils/cn";
import { Badge } from "@/view/components/ui/badge";
import { Button } from "@/view/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/view/components/ui/select";
import { Spinner } from "@/view/components/ui/spinner";

import {
  ALL_ASSIGNEES,
  CLAIM_NEXT_TAB,
  SupportListTab,
  useSupportQueue,
} from "./useSupportQueue";

interface SupportRequestQueueProps {
  tab: SupportListTab;
  selectedId?: string;
  onSelect?: (request: SupportRequest | null) => void;
  className?: string;
}

// quem abriu: vendedor cadastrado ou o contato digitado no formulário público
const requesterName = (request: SupportRequest) =>
  request.seller?.name || request.metadata?.contact?.name || "Não identificado";

interface QueueCardProps {
  request: SupportRequest;
  selected: boolean;
  onClick: () => void;
}

const QueueCard = ({ request, selected, onClick }: QueueCardProps) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={selected}
    className={cn(
      "w-full rounded-lg border border-gray01 bg-white p-4 text-left transition-colors hover:bg-gray-50",
      request.hasUnreadMessage && "border-primary/30 bg-primary/5 hover:bg-primary/10",
      selected && "border-primary bg-gray-50",
    )}
  >
    <div className="flex items-start justify-between gap-2">
      <span className="flex min-w-0 items-center gap-2 text-xs font-bold text-black01">
        <span className="truncate whitespace-nowrap">
          #{request.number || "-"}
        </span>
        {request.hasUnreadMessage && (
          <span
            aria-label="Nova mensagem"
            className="size-2 shrink-0 rounded-full bg-primary"
          />
        )}
      </span>
      <Badge
        className={cn(
          "shrink-0 whitespace-nowrap border-transparent",
          SUPPORT_STATUS_BADGE_CLASSNAME[request.status],
        )}
      >
        {SUPPORT_STATUS_LABEL[request.status]}
      </Badge>
    </div>

    <p className="mt-1 text-sm font-medium text-gray-500">
      {SUPPORT_REASON_LABEL[request.reason]}
    </p>

    <div className="flex items-center gap-2 mt-2">
      <span className="size-4 shrink-0 rounded-full bg-gray-200" />
      <p className="truncate text-xs font-medium text-gray-400">
        {requesterName(request)}
      </p>
    </div>

    <p className="mt-2 text-[11px] text-black04">
      Aberto {format(new Date(request.createdAt), "dd/MM · HH:mm")}
      {" · aguardando há "}
      {formatDistanceToNowStrict(new Date(request.createdAt), { locale: ptBR })}
    </p>
  </button>
);

export const SupportRequestQueue = ({
  tab,
  selectedId,
  onSelect,
  className,
}: SupportRequestQueueProps) => {
  const {
    queue,
    pendingCount,
    title,
    emptyMessage,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    hasUnassigned,
    hasAssigneeFilter,
    assignees,
    assignee,
    setAssignee,
    scrollRef,
    sentinelRef,
    claimNext,
    isClaiming,
    handleSelect,
  } = useSupportQueue({ tab, selectedId, onSelect });

  return (
    <aside
      className={cn(
        "flex min-h-0 flex-col rounded-xl border bg-card p-4",
        className,
      )}
    >
      <div className="flex items-center justify-between pb-3">
        <span className="text-sm font-semibold uppercase tracking-wide text-gray-500">
          {title}
        </span>
        <span className="text-xs text-black04">
          {pendingCount} {pendingCount === 1 ? "pendente" : "pendentes"}
        </span>
      </div>

      {tab === CLAIM_NEXT_TAB && (
        <Button
          className="w-full gap-2 rounded-lg text-white mb-2"
          onClick={() => claimNext()}
          isLoading={isClaiming}
          disabled={!hasUnassigned}
        >
          <Play className="h-3.5 w-3.5 fill-current" />
          Atender próximo chamado
        </Button>
      )}

      {hasAssigneeFilter && (
        <Select value={assignee} onValueChange={setAssignee}>
          <SelectTrigger className="mb-2 h-9 w-full rounded-lg" aria-label="Filtrar por responsável">
            <SelectValue placeholder="Responsável" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_ASSIGNEES}>Todos os responsáveis</SelectItem>
            {assignees.map((member) => (
              <SelectItem key={member.id} value={member.id}>
                {member.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      <div
        ref={scrollRef}
        className="mt-2 min-h-0 flex-1 space-y-3 overflow-y-auto"
      >
        {isLoading ? (
          <div className="flex justify-center py-10">
            <Spinner />
          </div>
        ) : queue.length === 0 ? (
          <p className="py-10 text-center text-sm text-black04">
            {emptyMessage}
          </p>
        ) : (
          <>
            {queue.map((request) => (
              <QueueCard
                key={request._id}
                request={request}
                selected={request._id === selectedId}
                onClick={() => handleSelect(request)}
              />
            ))}

            {hasNextPage && <div ref={sentinelRef} aria-hidden className="h-1" />}

            {isFetchingNextPage && (
              <div className="flex justify-center py-4">
                <Spinner />
              </div>
            )}
          </>
        )}
      </div>
    </aside>
  );
};
